import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { AbstractCrudService } from '../../../shared/components/crud/abstract-crud.service';
import { API_PATH, IBGE_API_PATH } from 'src/environments/environment';
import { RELATIVE_URL } from './crud-config';
import { IPartner } from 'src/app/domain/registration/models/IPartner';
import { tap, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PartnerService extends AbstractCrudService {
  override title = 'Empresa';
  override pluralTitle = 'Empresas';

  static isAdvancedFilterActive = false;
  static lastAppliedFilter = null;
  static params = { limit: 25, page: 0, filter: '' };
  static isNavigatingToNew = false;

  constructor(protected override httpClient: HttpClient) {
    super(httpClient, RELATIVE_URL);
  }

  public getPartnerById(id: string): Observable<IPartner> {
    return this.httpClient.get<IPartner>(`${API_PATH}${this.relativeUrl}/${id}`);
  }

  public getAllPartners(params?: any): Observable<IPartner[]> {
    const queryParams = params || { limit: 100, page: 0 };
    return this.httpClient.get<IPartner[]>(`${API_PATH}${this.relativeUrl}`, {
      params: queryParams
    });
  }

  public findByName(name: string): Observable<Object> {
    return this.httpClient.get<Object>(
      `${API_PATH}${this.relativeUrl}/byName/${name}`
    );
  }

  public getCnpjInfo(cnpj: string): Observable<IPartner> {
    return this.httpClient.get<IPartner>(
      `${API_PATH}${RELATIVE_URL}/cnpj/${cnpj}`
    );
  }

  public checkExistingCustomer(document: string): Observable<any> {
    return this.httpClient.get<any>(
      `${API_PATH}customers/document/${document}`
    ).pipe(
      catchError(error => {
        // Não faz nada em caso de erro, apenas retorna um observable vazio
        return of(null);
      })
    );
  }

  public findByFilter(filterForm: any, pagination: any): Observable<any[]> {
    let params: any = {
      getRelation: 0,
      limit: pagination?.limit || 36,
      page: pagination?.page || 0
    };

    if (pagination?.filter && typeof pagination.filter === 'string' && pagination.filter.trim() !== '') {
      params.filter = pagination.filter.trim();
    }

    const requestData = { ...filterForm, ...params };

    return this.httpClient.post<any[]>(
      `${API_PATH}${this.relativeUrl}/filter`,
      requestData
    );
  }

  public getPartnerByConsultant(): Observable<{ name: string, value: number }[]> {
    return this.httpClient.get<{ name: string, value: number }[]>(
      `${API_PATH}${this.relativeUrl}/partners-by-consultant`
    );
  }

  public getCitiesByUf(uf: string): Observable<any> {
    return this.httpClient.get(
      `${IBGE_API_PATH}localidades/estados/${uf}/municipios`
    );
  }

  public getStates(): Observable<any[]> {
    return this.httpClient.get<any[]>(`${IBGE_API_PATH}localidades/estados`);
  }

  public getCepInfo(cep: string): Observable<any> {
    return this.httpClient.get<any>(`${API_PATH}${RELATIVE_URL}/cep/${cep}`);
  }

  public changeActiveStatus(userId: string, isActive: boolean) {
    return this.httpClient.put(
      `${API_PATH}${this.relativeUrl}/change-status/${userId}`,
      { isActive }
    );
  }

  public changeWalletActiveStatus(userId: any, isWalletActive: boolean) {
    return this.httpClient.put(
      `${API_PATH}${this.relativeUrl}/change-wallet-status/${userId}`,
      { isWalletActive }
    );
  }

  public getGrowthPartners(): Observable<{ name: string; value: number }[]> {
    return this.httpClient.get<{ name: string; value: number }[]>(
      `${API_PATH}partners/growthw-partners`
    );
  }

  getCurrentMonthBirthdays() {
    return this.httpClient.get<{ count: number; list: { id: string; name: string; birthday: string; type: string; cellPhone: string }[] }>(
      `${API_PATH}partners/birthdays-current-month`
    );
  }

  getAllPartnersWithBirthdays() {
    return this.httpClient.get<{ count: number; list: { id: string; name: string; birthday: string; type: string; cellPhone: string; email: string; phone: string }[] }>(
      `${API_PATH}partners/all-with-birthdays`
    );
  }

  getContractTemplate(): Observable<any> {
    console.log('Buscando templates de contrato para parceiros');
    return this.httpClient.get(`${API_PATH}contracts/template`).pipe(
      tap(response => {
        console.log('Templates de contrato obtidos:',
          Object.keys(response).filter(k => k.includes('Template'))
        );
      }),
      catchError(error => {
        console.error('Erro ao obter templates de contrato:', error);
        // Retornar um objeto vazio com estrutura compatível para evitar erros
        return of({
          defaultTemplate: { content: '' },
          partnerTemplate: { content: '' },
          consultantTemplate: { content: '' },
          representativeTemplate: { content: '' },
          counterTemplate: { content: '' }
        });
      })
    );
  }

  saveContractTemplates(formData: FormData): Observable<any> {
    return this.httpClient.post(`${API_PATH}contracts/template/html`, formData);
  }

  getContractsByCustomer(customerId: string) {
    return this.httpClient.get(`${API_PATH}contracts/customer/${customerId}`);
  }

  cancelContract(contractId: string): Observable<any> {
    return this.httpClient.put(`${API_PATH}contracts/${contractId}/cancel`, {});
  }

  updateContractStatus(contractId: string): Observable<any> {
    return this.httpClient.get(`${API_PATH}contracts/${contractId}/status`);
  }

  updateAllContractsStatus(customerId: string): Observable<any> {
    return this.httpClient.put(`${API_PATH}contracts/customer/${customerId}/update-status`, {});
  }

  removeContract(contractId: string): Observable<any> {
    return this.httpClient.delete(`${API_PATH}contracts/${contractId}`);
  }

  downloadContract(contractId: string) {
    const downloadUrl = `${API_PATH}contracts/${contractId}/download`;

    window.open(downloadUrl, '_blank');
  }

  public getCommissionDashboard(filters: any): Observable<any> {
    return this.httpClient.post<any>(
      `${API_PATH}${this.relativeUrl}/commission-dashboard`,
      filters
    );
  }

  /**
   * Obtém a lista de sistemas disponíveis
   * @param filter Termo opcional para filtrar os sistemas
   * @returns Observable com a lista de sistemas
   */
  getSystems(filter?: string): Observable<any> {
    const url = `${API_PATH}systems`;
    const params = filter ? new HttpParams().set('filter', filter) : new HttpParams();
    return this.httpClient.get<any>(url, { params });
  }
}
